import { allowedDrivers } from './engine/schema'
import type { CaseInputs, SchemaEntry, View } from './engine/types'
import {
  ignoreGroupForLine,
  ignoreGroupForParent,
  isGroupIgnored,
  isGroupIgnoredAtAnyLocation,
} from './engine/ignoreGroups'
import {
  BAND_KEYS,
  FIXED_DRIVER_LABELS,
  GRAND_TOTAL_KEYS,
  LINKED_TOTAL_KEYS,
  METRIC_KEYS,
  gridDisplayUnit,
  type ComputedWeight,
  type GridRowModel,
} from './gridClasses'
import { LABOR_RATE_KEYS, LABOR_SUM_KEYS } from './engine/consolidate'
import { loadCashFlowSchema, type CashFlowEntry } from './engine/cashFlowSchema'

const PAYROLL_WAGES_KEY = 'is.payroll_wages'
const HIDDEN_GRID_KEYS = new Set(['is.boh', 'is.foh'])
const GREY_GROUP_HEADER_KEYS = new Set(['is.labor_boh', 'is.labor_foh', 'is.labor_salaried'])
const HOURLY_GA_KEYS = new Set([
  'is.hourly_fte_boh',
  'is.hourly_fte_foh',
  'is.avg_hourly_rate_boh',
  'is.avg_hourly_rate_foh',
])

function isLaborBuildupActive(draft: CaseInputs, view: View): boolean {
  return (
    view !== 'consolidated' &&
    draft.drivers[view]?.[PAYROLL_WAGES_KEY]?.active === 'labor_buildup'
  )
}

function isCarryForwardSubline(entry: SchemaEntry): boolean {
  return (
    entry.drivers.includes('carry_forward') &&
    !LINKED_TOTAL_KEYS.has(entry.key) &&
    entry.kind === 'input'
  )
}

function isFixedRow(key: string): boolean {
  return key in FIXED_DRIVER_LABELS
}

function isMetricRow(entry: SchemaEntry): boolean {
  return entry.kind === 'metric' || entry.italic || METRIC_KEYS.has(entry.key)
}

type BuildGridRowsOptions = {
  policyOwnedKeys?: Set<string>
  splitScheduleComputedAt?: string | null
}

function classifyEntry(
  entry: SchemaEntry,
  view: View,
  draft: CaseInputs,
  schema: SchemaEntry[],
  options: BuildGridRowsOptions,
): GridRowModel | null {
  if (entry.kind === 'check' || !entry.label.trim()) {
    return null
  }

  if (HIDDEN_GRID_KEYS.has(entry.key)) {
    return null
  }

  if (entry.kind === 'header') {
    if (BAND_KEYS.has(entry.key)) {
      return { kind: 'band', label: entry.label, sectionKey: entry.key }
    }
    const tone = GREY_GROUP_HEADER_KEYS.has(entry.key) ? 'grey' : 'green'
    return { kind: 'group', key: entry.key, label: entry.label, indent: entry.indent, tone }
  }

  if (view !== 'consolidated' && draft.consolidated_only.includes(entry.key)) {
    return {
      kind: 'greyed',
      key: entry.key,
      label: entry.label,
      indent: entry.indent,
      unit: gridDisplayUnit(entry.key, entry.unit),
      scope: 'row',
      driverLabel: 'Consolidated only',
    }
  }

  if (entry.kind === 'text') {
    return {
      kind: 'text',
      key: entry.key,
      label: entry.label,
      indent: entry.indent,
      unit: 'text',
    }
  }

  if (isFixedRow(entry.key)) {
    return {
      kind: 'computed',
      key: entry.key,
      label: entry.label,
      indent: entry.indent,
      unit: gridDisplayUnit(entry.key, entry.unit),
      weight: 'fixed',
      driverLabel: FIXED_DRIVER_LABELS[entry.key] ?? '',
    }
  }

  if (isMetricRow(entry)) {
    const indent = METRIC_KEYS.has(entry.key) ? Math.max(0, entry.indent - 1) : entry.indent
    const weight = entry.italic === false ? 'derived' : 'metric'
    return {
      kind: 'computed',
      key: entry.key,
      label: entry.label,
      indent,
      unit: gridDisplayUnit(entry.key, entry.unit),
      weight,
      driverLabel: '',
    }
  }

  if (entry.kind === 'subtotal') {
    const group = ignoreGroupForParent(schema, entry.key)
    if (group && isGroupIgnored(draft, view, group)) {
      const allowed = allowedDrivers(schema, entry.key, view, draft.consolidated_only, draft)
      if (allowed.length > 0) {
        const valueUnit = gridDisplayUnit(entry.key, entry.unit)
        return {
          kind: 'input',
          key: entry.key,
          label: entry.label,
          indent: entry.indent,
          unit: gridDisplayUnit(entry.key, entry.unit),
          valueUnit,
        }
      }
    }
    const weight = GRAND_TOTAL_KEYS.has(entry.key) ? 'total' : 'subtotal'
    return {
      kind: 'computed',
      key: entry.key,
      label: entry.label,
      indent: entry.indent,
      unit: gridDisplayUnit(entry.key, entry.unit),
      weight,
      driverLabel: '',
    }
  }

  if (entry.kind === 'input') {
    if (options.policyOwnedKeys?.has(entry.key)) {
      const valueUnit = gridDisplayUnit(entry.key, entry.unit)
      const computedAt = options.splitScheduleComputedAt
      return {
        kind: 'computed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: valueUnit,
        weight: 'fixed',
        driverLabel: 'Cash policy',
        driverHelp: /^bs\.(preferred_equity|allocated_income)_partner_\d+$/.test(entry.key)
          ? computedAt
            ? `Projected preferred return from the HP Threshold ledger (Dr Allocated Income / Cr Preferred Equity) · computed ${computedAt.slice(0, 10)}`
            : 'Projected preferred return from the HP Threshold ledger (Dr Allocated Income / Cr Preferred Equity)'
          : computedAt
            ? `Set from Ending Cash → Cash allocation · Waterfall split computed ${computedAt.slice(0, 10)}`
            : 'Set from Ending Cash → Cash allocation',
      }
    }
    if (entry.key === PAYROLL_WAGES_KEY && isLaborBuildupActive(draft, view)) {
      const valueUnit = gridDisplayUnit(entry.key, entry.unit)
      return {
        kind: 'computed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: valueUnit,
        weight: 'fixed',
        driverLabel: 'Build-up (FTE × rate × 80 h + salaries) × pay dates',
      }
    }
    if (
      (entry.key === 'is.boh' || entry.key === 'is.foh') &&
      isLaborBuildupActive(draft, view)
    ) {
      const valueUnit = gridDisplayUnit(entry.key, entry.unit)
      return {
        kind: 'computed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: valueUnit,
        weight: 'fixed',
        driverLabel: 'Derived from labor build-up',
      }
    }
    if (view === 'ga' && HOURLY_GA_KEYS.has(entry.key) && isLaborBuildupActive(draft, view)) {
      const valueUnit = gridDisplayUnit(entry.key, entry.unit)
      return {
        kind: 'greyed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: valueUnit,
        scope: 'row',
        driverLabel: '',
      }
    }
    const memberGroup = ignoreGroupForLine(schema, entry.key)
    if (
      view === 'consolidated' &&
      memberGroup &&
      isGroupIgnoredAtAnyLocation(draft, memberGroup)
    ) {
      return {
        kind: 'greyed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: gridDisplayUnit(entry.key, entry.unit),
        scope: 'projected',
        driverLabel: 'Ignored at a location',
      }
    }
    if (view === 'consolidated' && LABOR_SUM_KEYS.has(entry.key)) {
      const valueUnit = gridDisplayUnit(entry.key, entry.unit)
      return {
        kind: 'computed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: valueUnit,
        weight: 'fixed',
        driverLabel: 'Sum of locations',
      }
    }
    if (view === 'consolidated' && LABOR_RATE_KEYS.has(entry.key)) {
      const valueUnit = gridDisplayUnit(entry.key, entry.unit)
      return {
        kind: 'computed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit: valueUnit,
        weight: 'fixed',
        driverLabel: 'Weighted average',
      }
    }
    const allowed = allowedDrivers(schema, entry.key, view, draft.consolidated_only, draft)
    if (allowed.length === 0) {
      return null
    }
    const valueUnit = gridDisplayUnit(entry.key, entry.unit)
    return {
      kind: 'input',
      key: entry.key,
      label: entry.label,
      indent: entry.indent,
      unit: gridDisplayUnit(entry.key, entry.unit),
      valueUnit,
      driverLocked: isCarryForwardSubline(entry),
    }
  }

  return null
}

function isFirstBoldAfterSectionHeader(rows: GridRowModel[]): boolean {
  for (let i = rows.length - 1; i >= 0; i--) {
    const prior = rows[i]
    if (!prior) continue
    if (prior.kind === 'gap' || prior.kind === 'sectionPad') continue
    return prior.kind === 'band' || prior.kind === 'group'
  }
  return false
}

function pushCashFlowEntries(rows: GridRowModel[], cfSchema: CashFlowEntry[]) {
  for (const entry of cfSchema) {
    if (entry.kind === 'check') {
      continue
    }

    if (entry.kind === 'header') {
      if (entry.tone === 'band') {
        rows.push({ kind: 'band', label: entry.label, sectionKey: entry.key })
        continue
      }
      if (entry.tone === 'green') {
        if (entry.key !== 'cf.cash_flow_from_operations') {
          rows.push({ kind: 'gap' })
        }
        rows.push({
          kind: 'group',
          key: entry.key,
          label: entry.label,
          indent: entry.indent,
          tone: 'green',
        })
        continue
      }
      if (entry.tone === 'grey') {
        rows.push({
          kind: 'group',
          key: entry.key,
          label: entry.label,
          indent: entry.indent,
          tone: 'grey',
        })
        continue
      }
      continue
    }

    if (entry.kind === 'line' || entry.kind === 'subtotal' || entry.kind === 'metric') {
      if (entry.key === 'cf.fcf_ebitda_pct') {
        rows.push({ kind: 'gap' })
      }
      const weight = entry.weight as ComputedWeight
      const unit = gridDisplayUnit(entry.key, entry.unit)
      const row: GridRowModel = {
        kind: 'computed',
        key: entry.key,
        label: entry.label,
        indent: entry.indent,
        unit,
        weight,
        driverLabel: entry.driver_label,
      }
      if (weight === 'subtotal' || weight === 'total') {
        rows.push({ ...row, suppressTopRule: isFirstBoldAfterSectionHeader(rows) })
      } else {
        rows.push(row)
      }
      if (entry.key === 'cf.total_cash_eop') {
        rows.push({
          kind: 'cf_tie_check',
          key: 'cf.bs_cash_tie_check',
          label: 'Check',
          indent: entry.indent,
        })
      }
    }
  }
}

export function buildGridRows(
  schema: SchemaEntry[],
  view: View,
  draft: CaseInputs,
  options: BuildGridRowsOptions = {},
  cfSchema: CashFlowEntry[] = loadCashFlowSchema(),
): GridRowModel[] {
  const rows: GridRowModel[] = []
  const isEntries = schema.filter((e) => e.tab === 'IS')
  const bsEntries = schema.filter((e) => e.tab === 'BS')

  const pushEntries = (entries: SchemaEntry[]) => {
    for (const entry of entries) {
      if (view !== 'consolidated' && entry.bs_only) {
        continue
      }
      const row = classifyEntry(entry, view, draft, schema, options)
      if (row) {
        if (
          row.kind === 'computed' &&
          (row.weight === 'subtotal' || row.weight === 'total')
        ) {
          rows.push({ ...row, suppressTopRule: isFirstBoldAfterSectionHeader(rows) })
        } else {
          rows.push(row)
        }
      }
    }
  }

  if (view === 'consolidated') {
    pushEntries(isEntries)
    rows.push({ kind: 'gap' })
    pushEntries(bsEntries)
    rows.push({ kind: 'gap' })
    pushCashFlowEntries(rows, cfSchema)
  } else {
    pushEntries(isEntries)
  }

  const grossMarginIdx = rows.findIndex(
    (row) => row.kind === 'computed' && row.key === 'is.gross_margin_pct',
  )
  if (grossMarginIdx >= 0) {
    rows.splice(grossMarginIdx + 1, 0, { kind: 'gap' })
  }

  const netMarginIdx = rows.findIndex(
    (row) => row.kind === 'computed' && row.key === 'is.net_margin_pct',
  )
  if (netMarginIdx >= 0) {
    rows.splice(netMarginIdx + 1, 0, { kind: 'gap' })
  }

  return addSectionPads(rows)
}

function addSectionPads(rows: GridRowModel[]): GridRowModel[] {
  const out: GridRowModel[] = []
  let currentSection: string | null = null

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    if (!row) continue
    if (row.kind === 'band') {
      currentSection = row.sectionKey
      out.push(row)
      continue
    }

    const next = rows[i + 1]
    const isLastInSection =
      !next ||
      next.kind === 'band' ||
      (next.kind === 'gap' && rows[i + 2]?.kind === 'band')

    out.push(row)

    if (isLastInSection && currentSection && row.kind !== 'gap' && row.kind !== 'sectionPad') {
      out.push({ kind: 'sectionPad', sectionKey: currentSection })
    }
  }

  return out
}

export function gridBandSectionKeys(
  schema: SchemaEntry[],
  view: View,
  draft: CaseInputs,
  options: BuildGridRowsOptions = {},
): string[] {
  const keys: string[] = []
  for (const row of buildGridRows(schema, view, draft, options)) {
    if (row.kind === 'band') {
      keys.push(row.sectionKey)
    }
  }
  return keys
}

export function filterCollapsedRows(
  rows: GridRowModel[],
  collapsed: ReadonlySet<string>,
): GridRowModel[] {
  const out: GridRowModel[] = []
  let hide = false

  for (const row of rows) {
    if (row.kind === 'band') {
      hide = collapsed.has(row.sectionKey)
      out.push(row)
      continue
    }
    if (hide) {
      continue
    }
    out.push(row)
  }

  return out
}

export function sectionScrollTargets(rows: GridRowModel[]): Record<string, number> {
  const targets: Record<string, number> = {}
  rows.forEach((row, index) => {
    if (row.kind === 'band') {
      if (row.sectionKey === 'is.operational_data') {
        targets.operational = index
      } else if (row.sectionKey === 'is.income_statement') {
        targets.income = index
      } else if (row.sectionKey === 'bs.balance_sheet') {
        targets.balance = index
      }
    }
  })
  return targets
}
